import { AxiosResponse } from 'axios';
import { create } from 'zustand';

import { SECURE_API } from 'src/api/api';
import { IFormAdminUser, IUser } from 'src/interfaces/User';
import { runApiAction } from 'src/stores/lib/runApiAction';

interface UsersState {
  users: IUser[];
  isLoading: boolean;
  error: unknown | null;
  fetchUsers: () => Promise<void | AxiosResponse<IUser[]>>;
  createUser: (user: IFormAdminUser) => Promise<void>;
  updateUser: (user: IFormAdminUser) => Promise<IUser>;
  deleteUser: (id: string) => Promise<void>;
  deleteAllUsers: () => Promise<void>;
  reset: () => void;
}

const useUsersStore = create<UsersState>((set, get) => ({
  users: [],
  isLoading: false,
  error: null,

  fetchUsers: async () => {
    return runApiAction<UsersState, AxiosResponse<IUser[]>>({
      set,
      setLoading: true,
      action: () => SECURE_API.get<IUser[]>('/users'),
      onSuccess: ({ data }) => {
        set({ users: data, isLoading: false, error: null });
      },
      onError: error => {
        set({ users: [], isLoading: false, error });
      },
    });
  },

  createUser: async user => {
    const result = await runApiAction({
      set,
      setLoading: true,
      clearLoadingOnFinish: true,
      action: () =>
        SECURE_API.post<IUser>('/users/create', {
          name: user.name,
          email: user.email,
          password: user.password,
          role: user.roleId,
          dateFormat: user.dateFormat,
          timeFormat: user.timeFormat,
        }),
      onSuccess: () => {
        set({ error: null });
      },
      onError: error => {
        set({ error });
      },
    });

    if (result) {
      await get().fetchUsers();
    }
  },

  updateUser: async user => {
    const result = await runApiAction<UsersState, AxiosResponse<IUser>>({
      set,
      setLoading: true,
      clearLoadingOnFinish: true,
      action: () =>
        SECURE_API.put<IUser>(`/users/${user.id}`, {
          name: user.name,
          email: user.email,
          dateFormat: user.dateFormat,
          timeFormat: user.timeFormat,
          role: user.roleId,
          ...(user.newPassword ? { newPassword: user.newPassword } : {}),
        }),
      onSuccess: ({ data }) => {
        set({
          users: get().users.map(u => (u.id === data.id ? data : u)),
          error: null,
        });
      },
      onError: error => {
        set({ error });
      },
    });

    if (!result) {
      throw get().error ?? new Error('Failed to update user');
    }

    return result.data;
  },

  deleteUser: async id => {
    await runApiAction({
      set,
      setLoading: true,
      clearLoadingOnFinish: true,
      action: () => SECURE_API.delete(`/users/${id}`),
      onSuccess: () => {
        set({
          users: get().users.filter(u => u.id !== id),
          error: null,
        });
      },
      onError: error => {
        set({ error });
      },
    });
  },

  deleteAllUsers: async () => {
    await runApiAction({
      set,
      setLoading: true,
      clearLoadingOnFinish: true,
      action: () => SECURE_API.delete('/users/bulk'),
      onSuccess: () => {
        set({ users: [], error: null });
      },
      onError: error => {
        set({ error });
      },
    });
  },

  reset: () => set({ users: [], error: null, isLoading: false }),
}));

export default useUsersStore;
